import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';

// Fatturanti di Parcellazione (solo admin): nome, ordine, attivo. Un fatturante già usato non si elimina, si disattiva.
export default function Fatturanti() {
  const [fatturanti, setFatturanti] = useState([]);
  const [inModifica, setInModifica] = useState(null); // 'nuovo' | fatturante
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');

  const carica = useCallback(() => {
    api.get('/competenze/fatturanti').then(r => setFatturanti(r.data)).catch(err => setErrore(err.response?.data?.errore || 'Errore nel caricamento'));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const usi = f => [
    f.usi_competenze && `${f.usi_competenze} competenze`,
    f.usi_codici_cliente && `${f.usi_codici_cliente} codici cliente`,
    f.usi_codici_causale && `${f.usi_codici_causale} codici causale`,
    f.usi_predefinito && `${f.usi_predefinito} aziende (predefinito)`,
  ].filter(Boolean).join(' · ') || 'mai usato';
  const usato = f => f.usi_competenze + f.usi_codici_cliente + f.usi_codici_causale + f.usi_predefinito > 0;

  const elimina = async (f) => {
    setErrore('');
    if (!window.confirm(`Spostare nel Cestino il fatturante "${f.nome}"?`)) return;
    try { await api.delete(urlEliminazione(`/competenze/fatturanti/${f.id}`, f.version)); setMessaggio('Fatturante spostato nel Cestino'); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  return (
    <Layout>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Fatturanti</h2>
        <button onClick={() => setInModifica('nuovo')} style={stili.btn}>+ Nuovo fatturante</button>
      </div>
      <p style={{ color: '#555', fontSize: '0.88rem', marginTop: 0 }}>
        Un fatturante già usato non si elimina: si disattiva e sparisce dalle scelte per le nuove competenze, restando visibile nello storico. Il codice IVA GIS del fatturante e la nota si modificano da Impostazioni.
      </p>
      {errore && <div style={stili.errore}>{errore}</div>}
      {messaggio && <div style={{ background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' }}>{messaggio}</div>}
      <div style={{ overflowX: 'auto' }}>
      <table style={stili.table}>
        <thead><tr><th style={stili.th}>Ordine</th><th style={stili.th}>Nome</th><th style={stili.th}>Stato</th><th style={stili.th}>Utilizzo</th><th style={stili.th}>Azioni</th></tr></thead>
        <tbody>
          {fatturanti.map(f => (
            <tr key={f.id} style={{ ...stili.tr, opacity: f.attivo ? 1 : 0.55 }}>
              <td style={stili.td}>{f.ordine}</td>
              <td style={stili.td}>{f.nome}</td>
              <td style={stili.td}>{f.attivo ? 'Attivo' : 'Non attivo'}</td>
              <td style={{ ...stili.td, color: '#666' }}>{usi(f)}</td>
              <td style={{ ...stili.td, whiteSpace: 'nowrap' }}>
                <button onClick={() => setInModifica(f)} style={stili.btnSmall}>Modifica</button>
                <button onClick={() => elimina(f)} disabled={usato(f)} title={usato(f) ? 'Già usato: si può solo disattivare' : ''} style={{ ...stili.btnSmallDanger, opacity: usato(f) ? 0.4 : 1 }}>Elimina</button>
              </td>
            </tr>
          ))}
          {fatturanti.length === 0 && <tr><td colSpan={5} style={{ ...stili.td, textAlign: 'center', color: '#999' }}>Nessun fatturante</td></tr>}
        </tbody>
      </table>
      </div>
      {inModifica && <FormFatturante voce={inModifica === 'nuovo' ? null : inModifica} onChiudi={() => setInModifica(null)} onSalvato={(t) => { setInModifica(null); setMessaggio(t); carica(); }} />}
    </Layout>
  );
}

function FormFatturante({ voce, onChiudi, onSalvato }) {
  const daVoce = v => ({ nome: v?.nome || '', ordine: v?.ordine ?? '', attivo: v ? v.attivo : true, version: v?.version });
  const [form, setForm] = useState(daVoce(voce));
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);

  const salva = async (version) => {
    setErrore('');
    try {
      if (voce) await api.put(`/competenze/fatturanti/${voce.id}`, { nome: form.nome, ordine: form.ordine, attivo: form.attivo, version });
      else await api.post('/competenze/fatturanti', { nome: form.nome, ordine: form.ordine === '' ? undefined : form.ordine, attivo: form.attivo });
      onSalvato(voce ? 'Fatturante aggiornato' : 'Fatturante creato');
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    }
  };

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '440px' }}>
        <h3 style={{ marginTop: 0 }}>{voce ? 'Modifica fatturante' : 'Nuovo fatturante'}</h3>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.campo}>
            <label style={stili.label}>Nome</label>
            <input autoFocus required value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} style={stili.input} />
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Ordine (numero, per l'elenco)</label>
            <input type="number" value={form.ordine} onChange={e => setForm(f => ({ ...f, ordine: e.target.value }))} style={stili.input} />
          </div>
          <label style={{ fontSize: '0.88rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <input type="checkbox" checked={form.attivo} onChange={e => setForm(f => ({ ...f, attivo: e.target.checked }))} />
            Attivo (se tolto non compare più tra le scelte per le nuove competenze)
          </label>
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn}>Salva</button>
          </div>
        </form>
      </div>
      {conflitto && <ConflittoVersione conflitto={conflitto} onRicarica={() => { setForm(daVoce(conflitto.attuale)); setConflitto(null); }} onSovrascrivi={() => salva(conflitto.attuale.version)} />}
    </div>
  );
}
